import SEO from '../components/SEO';

const PrivacyPage = () => {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Learn how AdOnRoads protects your privacy and handles your data."
      />

      <div className="pt-24 pb-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-secondary mb-6">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Effective Date: January 1, 2025</p>

          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">1. Introduction</h2>
              <p className="text-gray-700">
                At AdOnRoads, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, and safeguard your data when you use our Platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">2. Information We Collect</h2>
              <p className="text-gray-700 mb-4">
                We collect minimal user data necessary for Beta participation and service delivery:
              </p>
              <h3 className="text-xl font-bold text-secondary mb-3 mt-4">Information You Provide:</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Name, email address, company name, and city (Beta signup)</li>
                <li>Account credentials and profile information</li>
                <li>Billing and payment information (processed securely)</li>
                <li>Campaign materials and booking details</li>
                <li>Communications with our support team</li>
              </ul>
              <h3 className="text-xl font-bold text-secondary mb-3 mt-4">Automatically Collected Information:</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Device information (browser type, operating system)</li>
                <li>Usage data (pages visited, features used)</li>
                <li>IP address and location data</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">
                We use your information to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide and improve our services</li>
                <li>Process bookings and payments</li>
                <li>Communicate with you about your account and campaigns</li>
                <li>Send Beta program updates and important notifications</li>
                <li>Provide customer support</li>
                <li>Analyze platform usage and improve user experience</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">4. Data Sharing and Disclosure</h2>
              <p className="text-gray-700 mb-4">
                <strong>We will never sell your personal data.</strong> We may share your information only in these limited circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>With Billboard Partners:</strong> Name and contact information to facilitate campaign execution</li>
                <li><strong>With Payment Processors:</strong> Payment information to process transactions securely</li>
                <li><strong>With Service Providers:</strong> Trusted third parties who assist in operating our Platform</li>
                <li><strong>For Legal Compliance:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">5. Data Security</h2>
              <p className="text-gray-700">
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
                <li>Encrypted data transmission (SSL/TLS)</li>
                <li>Secure data storage with regular backups</li>
                <li>Access controls and authentication</li>
                <li>Regular security audits and updates</li>
                <li>PCI-DSS compliant payment processing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">6. Your Privacy Rights</h2>
              <p className="text-gray-700 mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your data (subject to legal requirements)</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Data Portability:</strong> Receive your data in a structured format</li>
                <li><strong>Object:</strong> Object to certain data processing activities</li>
              </ul>
              <p className="text-gray-700 mt-4">
                To exercise these rights, email us at <a href="mailto:privacy@adonroads.com" className="text-accent hover:underline">privacy@adonroads.com</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">7. Cookies and Tracking</h2>
              <p className="text-gray-700">
                We use cookies and similar technologies to enhance your experience. You can control cookie preferences through your browser settings. Disabling cookies may affect Platform functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">8. Data Retention</h2>
              <p className="text-gray-700">
                We retain your data for as long as necessary to provide services and comply with legal obligations. Inactive accounts may be archived or deleted after 24 months of inactivity.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">9. Children's Privacy</h2>
              <p className="text-gray-700">
                AdOnRoads is not intended for users under 18 years of age. We do not knowingly collect data from children. If you believe a child has provided us with personal information, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">10. International Data Transfers</h2>
              <p className="text-gray-700">
                Your data may be processed in countries outside India. We ensure appropriate safeguards are in place to protect your data in accordance with applicable laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">11. Compliance</h2>
              <p className="text-gray-700">
                This Privacy Policy is compliant with:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
                <li>Indian Digital Personal Data Protection Act (DPDP)</li>
                <li>General Data Protection Regulation (GDPR) principles</li>
                <li>Information Technology Act, 2000</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">12. Changes to Privacy Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy periodically. We will notify you of significant changes via email or Platform notification. Continued use after changes constitutes acceptance.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-secondary mb-4">13. Contact Us</h2>
              <p className="text-gray-700">
                For privacy-related questions or requests, contact us at:
              </p>
              <p className="text-gray-700 mt-4">
                Email: <a href="mailto:privacy@adonroads.com" className="text-accent hover:underline">privacy@adonroads.com</a><br/>
                Data Protection Officer: AdOnRoads, Pune, India
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPage;